/**
 * System Prompts API service for CRUD operations on system AI prompts.
 * Prompts are stored in DynamoDB (SystemPrompts table).
 * Admin-only for create/update/delete operations.
 */

import { BaseApiService } from './BaseApiService';
import { API_URL } from '@/lib/apiConfig';

export interface SystemPrompt {
  promptId: string;
  promptName: string;
  category: 'documentation' | 'analysis' | 'processing';
  description: string;
  template: string;
  modelId: string;
  maxTokens: number;
  anthropicVersion: string;
  version: string;
  isActive: boolean;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

export interface SaveSystemPromptRequest {
  promptId?: string;
  promptName: string;
  category: string;
  description: string;
  template: string;
  modelId: string;
  maxTokens: number;
  anthropicVersion: string;
  version: string;
  isActive: boolean;
  metadata: Record<string, unknown>;
}

class SystemPromptsApiService extends BaseApiService {
  constructor() {
    super(API_URL, 'SystemPromptsApi');
  }

  async listSystemPrompts(): Promise<SystemPrompt[]> {
    this.log.debug('Listing system prompts');
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/admin/system-prompts`, { method: 'GET', headers });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to list system prompts', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to list system prompts');
    }

    const data = await response.json();
    return data.prompts || [];
  }

  async getSystemPrompt(promptId: string): Promise<SystemPrompt> {
    this.log.debug('Getting system prompt', { promptId });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/admin/system-prompts/${promptId}`, { method: 'GET', headers });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to get system prompt', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to get system prompt');
    }

    const data = await response.json();
    return data.prompt;
  }

  async createSystemPrompt(request: SaveSystemPromptRequest): Promise<SystemPrompt> {
    this.log.debug('Creating system prompt', { promptId: request.promptId });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/admin/system-prompts`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to create system prompt', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to create system prompt');
    }

    const data = await response.json();
    return data.prompt;
  }

  async updateSystemPrompt(promptId: string, request: SaveSystemPromptRequest): Promise<SystemPrompt> {
    this.log.debug('Updating system prompt', { promptId });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/admin/system-prompts/${promptId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to update system prompt', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to update system prompt');
    }

    const data = await response.json();
    return data.prompt;
  }

  async deleteSystemPrompt(promptId: string): Promise<void> {
    this.log.debug('Deleting system prompt', { promptId });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/admin/system-prompts/${promptId}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to delete system prompt', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to delete system prompt');
    }

    this.log.info('System prompt deleted', { promptId });
  }
}

export const systemPromptsApi = new SystemPromptsApiService();
export default systemPromptsApi;
